import { DEFAULT_PARSER_CONFIG } from "../constants/defaultParserConfig";
import { buildOutputTable, flattenXmlObject, flattenXmlToRows } from "./xmlFlatten";
import { createXmlParser, parseXmlString } from "./xmlParserService";
import { getSelectedXmlCells, writeParsedColumns, writeTableAtSelection } from "./excelService";
import { ensureParserAccess } from "./subscriptionService";
import { readXmlFiles } from "./fileImportService";

function createParser(parserConfig) {
  return createXmlParser({ ...DEFAULT_PARSER_CONFIG, ...parserConfig });
}

async function guardAccess() {
  const access = await ensureParserAccess();
  if (!access.allowed) {
    throw new Error(access.reason);
  }
  return access;
}

export async function runXmlToColumns(parserConfig, onProgress) {
  await guardAccess();
  const parser = createParser(parserConfig);

  onProgress?.({ phase: "reading", message: "Reading selected cells..." });
  const selection = await getSelectedXmlCells();

  if (selection.cells.length === 0) {
    throw new Error("No XML data found in the selected range.");
  }

  const flatRows = [];
  const errors = [];

  selection.cells.forEach((cell, index) => {
    onProgress?.({
      phase: "parsing",
      message: `Parsing row ${index + 1} of ${selection.cells.length}...`,
      current: index + 1,
      total: selection.cells.length,
    });

    try {
      const parsed = parseXmlString(parser, cell.value);
      const columns = flattenXmlObject(parsed);
      flatRows.push({ rowIndex: cell.rowIndex, columns });
    } catch (error) {
      flatRows.push({ rowIndex: cell.rowIndex, columns: {} });
      errors.push({
        rowIndex: cell.rowIndex,
        label: `Row ${cell.rowIndex + 1}`,
        message: error?.message || "Invalid XML.",
      });
    }
  });

  const { headers, values } = buildOutputTable(flatRows);

  onProgress?.({ phase: "writing", message: "Writing columns to Excel..." });

  const sourceColumnIndex = selection.cells[0].columnIndex;
  const sourceRowIndices = flatRows.map((row) => row.rowIndex);

  await writeParsedColumns({
    worksheetName: selection.worksheetName,
    sourceColumnIndex,
    sourceRowIndices,
    headers,
    values,
  });

  return {
    mode: "selection",
    parsedCount: flatRows.length - errors.length,
    errorCount: errors.length,
    columnCount: headers.length,
    errors,
    sourceAddress: selection.sourceAddress,
  };
}

export async function runXmlFolderToColumns(parserConfig, fileList, onProgress) {
  await guardAccess();
  const parser = createParser(parserConfig);

  const files = await readXmlFiles(fileList, onProgress);

  const flatRows = [];
  const errors = [];

  files.forEach((file, index) => {
    onProgress?.({
      phase: "parsing",
      message: `Parsing file ${index + 1} of ${files.length}...`,
      current: index + 1,
      total: files.length,
    });

    const baseColumns = { "source.file": file.relativePath || file.fileName };

    try {
      const parsed = parseXmlString(parser, file.value);
      const rowColumnsList = flattenXmlToRows(parsed);

      if (rowColumnsList.length === 0) {
        flatRows.push({ columns: baseColumns });
      } else {
        rowColumnsList.forEach((columns) => {
          flatRows.push({ columns: { ...baseColumns, ...columns } });
        });
      }
    } catch (error) {
      flatRows.push({ columns: baseColumns });
      errors.push({
        fileName: file.fileName,
        label: file.fileName,
        message: error?.message || "Invalid XML.",
      });
    }
  });

  const { headers, values } = buildOutputTable(flatRows);

  onProgress?.({ phase: "writing", message: "Writing columns to Excel..." });
  const written = await writeTableAtSelection({ headers, values });

  return {
    mode: "folder",
    parsedCount: flatRows.length - errors.length,
    errorCount: errors.length,
    columnCount: headers.length,
    fileCount: files.length,
    errors,
    sourceAddress: written.address,
  };
}
